// agency.service.ts
import { apiClient } from '../api-client';
import { TokenService } from '../auth/token.service';
import { getUserAttributesFromToken } from '../auth/cognito.service';
import { ApiResponse, isApiError } from '../../dtos/common/api.dto';
import {
  Agency,
  AgencyData,
  AgencyFieldError,
  EVVOnboardingStepStatus,
  EvvOnboardingDto,
  EvvOnboardingStepDto,
  EvvOnboardingUpdateRequest,
} from '../../dtos/admin/agency.dto';

// Re-export AgencyData for backwards compatibility
export type { AgencyData } from '../../dtos/admin/agency.dto';

/**
 * Create a new agency
 * @param agencyData The agency data to create
 * @returns A Promise that resolves to the created Agency
 */
export const createAgency = async (agencyData: AgencyData): Promise<Agency> => {
  try {
    const response: ApiResponse<Agency> = await apiClient.post('/agencies', agencyData);

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to create agency');
  } catch (error) {
    console.error('Error creating agency:', error);
    throw error;
  }
};

/**
 * Update an existing agency
 * @param agencyId The ID of the agency to update
 * @param agencyData The updated agency data
 * @returns A Promise that resolves to the updated Agency
 */
export const updateAgency = async (agencyId: string, agencyData: AgencyData): Promise<Agency> => {
  try {
    const response: ApiResponse<Agency> = await apiClient.put(`/agencies/${agencyId}`, agencyData);

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to update agency');
  } catch (error) {
    console.error(`Error updating agency ${agencyId}:`, error);
    throw error;
  }
};

/**
 * Get agency by agency ID
 * @param agencyId The ID of the agency to retrieve
 * @returns A Promise that resolves to the Agency
 */
export const getAgencyById = async (agencyId: string): Promise<Agency> => {
  try {
    const response: ApiResponse<Agency> = await apiClient.get(`/agencies/${agencyId}`);

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to fetch agency');
  } catch (error) {
    console.error(`Error fetching agency ${agencyId}:`, error);
    throw error;
  }
};

/**
 * Get agency by tenant ID
 * @param tenantId The tenant ID associated with the agency
 * @returns A Promise that resolves to the Agency
 */
export const getAgencyByTenantId = async (tenantId: string): Promise<Agency | null> => {
  try {
    const response: ApiResponse<Agency> = await apiClient.get(`/agencies/tenant/${tenantId}`);

    if (response.success && response.data) {
      return response.data;
    }

    // Handle 404 case - no agency found for this tenant
    if (response.error?.code === 'NOT_FOUND') {
      return null;
    }

    throw new Error(response.error?.message || 'Failed to fetch agency for tenant');
  } catch (error: unknown) {
    // If it's a 404, we'll return null to indicate no agency exists for this tenant
    if (isApiError(error) && (error.statusCode === 404 || error.code === 'NOT_FOUND')) {
      return null;
    }
    console.error('Error fetching agency for tenant:', error instanceof Error ? error.message : 'unknown error');

    throw error;
  }
};

/**
 * Get the agency for the current user's tenant
 * This is a convenience method that reads the tenant ID from the signed-in user's ID token
 * (never from storage) and fetches the associated agency
 * @returns A Promise that resolves to the Agency or null if not found
 */
export const getCurrentAgency = async (): Promise<Agency | null> => {
  try {
    const idToken = TokenService.getIdToken();
    const tenantId = idToken ? getUserAttributesFromToken(idToken)?.tenantId : null;

    if (!tenantId) {
      return null;
    }

    const agency = await getAgencyByTenantId(tenantId);
    return agency;
  } catch (error) {
    console.error('Error fetching current agency:', error);
    throw error;
  }
};

// ---------------------------------------------------------------------------
// Phase 5: EVV onboarding checklist (GET/PUT /agencies/{agencyId}/evv-onboarding)
// ---------------------------------------------------------------------------

const STEP_STATUSES: EVVOnboardingStepStatus[] = ['NOT_STARTED', 'IN_PROGRESS', 'DONE'];

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function normalizeStep(raw: unknown): EvvOnboardingStepDto | null {
  if (!raw || typeof raw !== 'object') return null;
  const step = raw as Record<string, unknown>;
  // The Phase 5 contract uses `key`; older records used `id`
  const key = asString(step.key) ?? asString(step.id);
  if (!key) return null;
  const status = STEP_STATUSES.includes(step.status as EVVOnboardingStepStatus)
    ? (step.status as EVVOnboardingStepStatus)
    : 'NOT_STARTED';
  return {
    key,
    label: asString(step.label) ?? key,
    description: asString(step.description),
    status,
    resourceUrl: asString(step.resourceUrl),
    updatedAt: asString(step.updatedAt),
    updatedBy: asString(step.updatedBy),
  };
}

/** Accepts the Phase 5 shape and the older `{ evvOnboarding: { steps } }` shape. */
function normalizeEvvOnboarding(raw: unknown): EvvOnboardingDto {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const nested = (data.evvOnboarding && typeof data.evvOnboarding === 'object'
    ? data.evvOnboarding
    : {}) as Record<string, unknown>;
  const rawSteps = Array.isArray(data.steps) ? data.steps : Array.isArray(nested.steps) ? nested.steps : [];
  const steps = rawSteps
    .map(normalizeStep)
    .filter((step): step is EvvOnboardingStepDto => step !== null);
  const admin = (data.evvSystemAdmin && typeof data.evvSystemAdmin === 'object'
    ? data.evvSystemAdmin
    : {}) as Record<string, unknown>;
  const complete = typeof data.complete === 'boolean'
    ? data.complete
    : typeof nested.complete === 'boolean'
      ? nested.complete
      : steps.length > 0 && steps.every((step) => step.status === 'DONE');
  return {
    steps,
    complete,
    hhaxProviderId: asString(data.hhaxProviderId),
    evvProviderNpi: asString(data.evvProviderNpi),
    evvProviderApi: asString(data.evvProviderApi),
    evvSystemAdmin: { name: asString(admin.name), email: asString(admin.email) },
  };
}

/** Load the EVV onboarding checklist (steps, labels and resource links come from the API). */
export const getEvvOnboarding = async (agencyId: string): Promise<EvvOnboardingDto> => {
  const response: ApiResponse<unknown> = await apiClient.get(`/agencies/${agencyId}/evv-onboarding`);
  if (response.success) {
    return normalizeEvvOnboarding(response.data);
  }
  throw new Error(response.error?.message || 'Failed to load the EVV checklist');
};

/**
 * Save an EVV onboarding change: a single step status, the HHAX provider ID, the EVV-linked
 * NPI / API (Phase 22; null or '' clears), or the EVV system admin.
 * Steps are saved only through this route, never through the generic agency PUT.
 */
export const updateEvvOnboarding = async (
  agencyId: string,
  update: EvvOnboardingUpdateRequest
): Promise<EvvOnboardingDto> => {
  const body: Record<string, unknown> = {};
  if (update.stepUpdate) {
    // `id` is sent as well so a backend that still reads stepUpdate.id applies the change
    body.stepUpdate = { key: update.stepUpdate.key, id: update.stepUpdate.key, status: update.stepUpdate.status };
  }
  if (update.hhaxProviderId !== undefined) body.hhaxProviderId = update.hhaxProviderId;
  if (update.evvProviderNpi !== undefined) body.evvProviderNpi = update.evvProviderNpi;
  if (update.evvProviderApi !== undefined) body.evvProviderApi = update.evvProviderApi;
  if (update.evvSystemAdmin !== undefined) body.evvSystemAdmin = update.evvSystemAdmin;
  const response: ApiResponse<unknown> = await apiClient.put(`/agencies/${agencyId}/evv-onboarding`, body);
  if (response.success) {
    return normalizeEvvOnboarding(response.data);
  }
  throw new Error(response.error?.message || 'Failed to save the EVV checklist');
};

// ---------------------------------------------------------------------------
// Phase 5: agency profile error helpers
// ---------------------------------------------------------------------------

/** Field errors from a 400 AGENCY_VALIDATION response (empty for any other error). */
export function getAgencyFieldErrors(error: unknown): AgencyFieldError[] {
  if (!isApiError(error) || error.statusCode !== 400) return [];
  const fields = error.details?.fields;
  if (!Array.isArray(fields)) return [];
  return fields
    .filter((f): f is { field: string; message: string } =>
      !!f && typeof f === 'object'
      && typeof (f as Record<string, unknown>).field === 'string'
      && typeof (f as Record<string, unknown>).message === 'string')
    .map((f) => ({ field: f.field, message: f.message }));
}

/**
 * Missing profile labels (e.g. "Taxonomy") from a 422 AGENCY_PROFILE_INCOMPLETE response.
 * Returns null when the error is not that response.
 */
export function getAgencyProfileMissing(error: unknown): string[] | null {
  if (!isApiError(error)) return null;
  if (error.domainCode !== 'AGENCY_PROFILE_INCOMPLETE' && error.details?.code !== 'AGENCY_PROFILE_INCOMPLETE') {
    return null;
  }
  const missing = error.details?.missing;
  return Array.isArray(missing) ? missing.filter((m): m is string => typeof m === 'string') : [];
}

/** "12345" or "12345-6789" for printing on forms (zipPlus4 may sit on address or the agency). */
export function formatAgencyZip(agency: Pick<Agency, 'address' | 'zipPlus4'>): string {
  const zip = agency.address?.zipCode || '';
  const plus4 = agency.address?.zipPlus4 || agency.zipPlus4 || '';
  if (!zip || zip.includes('-') || !plus4) return zip;
  return `${zip}-${plus4}`;
}
